import "./ThinkingTicker.css";
import { TextMono } from "@ui/typography/TextMono";
import { createEffect, createSignal, onCleanup, Show } from "solid-js";

const MIN_TITLE_DISPLAY_MS = 2500;
const TITLE_ENTER_ANIMATION_MS = 250;

export const ThinkingTicker = (props: { titles: readonly string[] }) => {
	const [displayTitle, setDisplayTitle] = createSignal<string | undefined>();
	const [isEntering, setIsEntering] = createSignal(false);

	let consumedCount = 0;
	let pendingTitles: string[] = [];
	let processing = false;
	let timeoutId: ReturnType<typeof setTimeout> | undefined;

	const clearTimer = () => {
		if (timeoutId !== undefined) {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}
	};

	const schedule = (fn: () => void, ms: number) => {
		clearTimer();
		timeoutId = setTimeout(fn, ms);
	};

	const showNextTitle = () => {
		const title = pendingTitles.shift();
		if (!title) {
			processing = false;
			return;
		}

		setIsEntering(false);
		schedule(() => {
			setDisplayTitle(title);
			setIsEntering(true);
			schedule(() => {
				schedule(showNextTitle, MIN_TITLE_DISPLAY_MS);
			}, TITLE_ENTER_ANIMATION_MS);
		}, 0);
	};

	const enqueueTitles = () => {
		const titles = props.titles;
		if (titles.length > consumedCount) {
			pendingTitles.push(...titles.slice(consumedCount));
			consumedCount = titles.length;
		}

		if (!processing && pendingTitles.length > 0) {
			processing = true;
			showNextTitle();
		}
	};

	createEffect(() => {
		enqueueTitles();
	});

	onCleanup(() => {
		clearTimer();
		processing = false;
		pendingTitles = [];
	});

	return (
		<Show when={displayTitle()}>
			{(title) => (
				<div
					class="thinking-ticker"
					style={{
						"--thinking-ticker-enter-duration": `${TITLE_ENTER_ANIMATION_MS}ms`,
					}}
				>
					<div class="thinking-ticker__viewport">
						<div
							class="thinking-ticker__line"
							classList={{ "is-entering": isEntering() }}
						>
							<TextMono
								as="span"
								class="thinking-ticker__text"
								color="fg3"
							>
								{title()}
							</TextMono>
						</div>
					</div>
				</div>
			)}
		</Show>
	);
};
